"use client"

// PR #1256 — /ambassadors recruitment landing page (EN + Swiss-DE).
// Review tracking: https://linear.app/aheadhealth/issue/AHD-6403
import { m } from "framer-motion"
import Image from "next/image"
import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import {
  ApplePodcastsLogo,
  Handshake,
  InstagramLogo,
  Pill,
  TestTube,
  TiktokLogo,
  VideoConference,
  YoutubeLogo,
} from "@phosphor-icons/react"
import type { Icon as PhosphorIcon } from "@phosphor-icons/react"

import { blobMediaUrl } from "@/lib/blob-url"
import { CountryGate } from "@/components/layout/country-gate"
import { ArrowRightIcon, Button } from "@/components/ui/button"
import { AheadArrowIcon } from "@/components/ui/icons"
import { FaqSection, type FaqAccordionItem } from "@/components/ui/faq-section"
import { TestimonialsSection } from "../../_components/testimonials-section"
import type { Testimonial } from "@/lib/cms"

import { ambassadorsKeys as k } from "@/i18n/ambassadors-keys"
import { useLocalePath } from "@/lib/use-localized-navigation"
import {
  INCLUDED_KEYS,
  IncludedFigmaGrid,
} from "../../_components/included-grid"
import {
  ActionPlanTile,
  BioAgeTile,
  BiomarkersTile,
  DumbbellIcon,
  FloatingTile,
  TestTubeIcon,
} from "./ambassadors-floating-tiles"

const AMBASSADOR_FORM_URL = "https://ambassador-form.zite.so/"

/**
 * Trailing arrow icon for CTA buttons. Slides right 1 unit on the
 * parent `.group`'s hover (200ms, expo-out) — the marketing-site's
 * standard "nudge" pattern used everywhere CTAs lead off-page. Pair
 * with `<Button className="group" iconRight={<NudgeArrow />}>…`.
 */
function NudgeArrow() {
  return (
    <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
  )
}

/**
 * Tiny floating glass circle containing a single Phosphor brand logo —
 * the platform "cue" tiles mixed in with the data + action tiles in
 * the hero. Circular (40px) glass dot vs the ~230px-wide data cards,
 * so they read as accent indicators. Icon stays at 22px so it nearly
 * fills the dot — the logo is the point, not the chrome.
 *
 * Chrome treatment: a *layer-blurred* white circle (Figma "layer
 * blur" → CSS `filter: blur()`) sits behind a sharp icon. The blur
 * on the chrome itself reads as a soft halo regardless of what's
 * behind it; backdrop-blur was the wrong tool here because the
 * cyclist sky behind these tiles is too uniform to refract.
 *
 * Structure: relative-positioned wrapper sized 40px → an absolutely
 * positioned blurred disc (filter: blur(6px)) + the icon on top.
 * The disc is slightly inset so the blur softens to nothing at the
 * edge of the wrapper rather than getting clipped.
 */
function PlatformTile({ Icon }: { Icon: PhosphorIcon }) {
  return (
    <div className="relative flex size-10 items-center justify-center">
      <div
        className="pointer-events-none absolute inset-[-2px] rounded-full bg-white/55"
        style={{ filter: "blur(6px)" }}
        aria-hidden="true"
      />
      {/* Icon sits at 70% opacity so it reads as a recessive accent
          rather than the page's primary text — keeps the cyclist + the
          data tiles as the focal points and the platform cues as
          background detail. */}
      <Icon
        size={22}
        weight="regular"
        className="relative text-neutral-900/70"
      />
    </div>
  )
}

/**
 * Dev-only flag: when true, every FloatingTile renders a small
 * coordinate label so we can eyeball-tune the platform tile positions
 * before locking them in. Flip to `false` (or delete the prop on the
 * tiles) once positions are finalised — keeps the markers from
 * shipping to prod by accident.
 */
const SHOW_TILE_POSITIONS = false

const STEPS = [
  { titleKey: k.step1Title, descKey: k.step1Desc, Icon: VideoConference },
  { titleKey: k.step2Title, descKey: k.step2Desc, Icon: Handshake },
  { titleKey: k.step3Title, descKey: k.step3Desc, Icon: TestTube },
  { titleKey: k.step4Title, descKey: k.step4Desc, Icon: InstagramLogo },
] satisfies {
  titleKey: string
  descKey: string
  Icon: PhosphorIcon
}[]

const PROFILE_CHIPS = [
  { labelKey: k.profileTrack1, icon: "ecg-heart" },
  { labelKey: k.profileTrack2, icon: "wheat" },
  { labelKey: k.profileTrack3, icon: "metabolic" },
] as const

export default function AmbassadorsClient({
  initialTestimonials,
  initialFaqs,
}: {
  initialTestimonials?: Testimonial[]
  initialFaqs: FaqAccordionItem[]
}) {
  const t = useTranslations("ambassadors")
  const lp = useLocalePath()

  return (
    <>
      {/* ──────────────────────── Section 1 — Hero ──────────────────────── */}
      {/*
        Negative margin-top pulls the section up behind the sticky navbar
        (and the top banner). The navbar's `bg-white/70 backdrop-blur-md`
        lets the photo show through as a glass-over-image effect, matching
        the Figma intent. Internal padding-top keeps the badge clear of
        the navbar pill.
      */}
      <section className="relative -mt-[72px] overflow-hidden pt-[144px] pb-[400px] text-center md:min-h-[980px] md:pt-[176px] md:pb-20">
        {/* Background photo — cyclist on lakeside road, fills the hero area
            so future floating elements (cards from /membership pattern) can
            sit on top. */}
        <Image
          src={blobMediaUrl("/images/marketing/ambassadors-hero-cyclist.png")}
          alt=""
          fill
          priority
          sizes="100vw"
          aria-hidden="true"
          className="object-cover object-bottom"
        />
        {/* Gradient overlay — per Figma 0:53. Lightens the top-left so the
            headline + subtitle remain legible against the photo. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(121.09deg, rgb(224, 236, 242) 22.654%, rgba(255, 255, 255, 0) 49.505%), linear-gradient(180deg, rgba(215, 236, 251, 0.67) 0%, rgba(255, 255, 255, 0) 62.453%)",
          }}
        />
        <div className="relative mx-auto max-w-[1440px] px-4 md:px-10">
          {/* Floating tiles — positions baked in as percentages of the
              1440px desktop content width, so they shrink toward the
              cyclist gracefully on tablet and hide entirely below `md`.
              Stagger + subtle Y bob makes them feel alive without being
              distracting. */}
          {/*
            Top two tiles (Calcium 464, Biomarkers 424) sit 16px lower
            than their original y values to make room for German's
            longer 3-line hero title — the bottom three (BioAge 585,
            Potassium 563, Exercise 684) stay put so the lower cluster
            still anchors the cyclist's mid-line.
          */}
          {/*
            Platform "cue" tiles — small Phosphor brand-logo circles
            placed around the data + action tiles to signal "we
            partner with creators on these channels". 40px glass dots
            (vs ~230px-wide data tiles) read as accents, not cards.

            Rendered BEFORE the data tiles in the JSX (round 7
            feedback) so the soft halos paint UNDER the data + action
            cards. Any incidental halo bleed (the layer-blurred disc
            extends ~14px past the 40px wrapper) is hidden behind the
            opaque data tile rather than smudging over its content.
            All siblings inside the same absolute container; source
            order is the only thing controlling z-stacking here.

            Positions were drag-tuned with SHOW_TILE_POSITIONS — flip
            that flag back on if you want to retune. Coordinates sit
            in the same percentage-of-1440 / px-from-hero-top space as
            the data tiles, so the whole composition scales together.

            Animation + responsive: identical to the data tiles since
            they share FloatingTile — same blur-to-focus entrance,
            same `hidden lg:block` gate (hidden below 1024px). Delays
            interleave with the data tiles (0.6–1.0) so the platform
            circles fade in as part of the same stagger.
          */}
          {(
            [
              {
                Icon: InstagramLogo,
                name: "ig",
                pos: { xPct: 12.11, y: 675 },
                delay: 0.65,
              },
              {
                Icon: TiktokLogo,
                name: "tt",
                pos: { xPct: 28.52, y: 530 },
                delay: 0.75,
              },
              {
                Icon: YoutubeLogo,
                name: "yt",
                pos: { xPct: 73.07, y: 535 },
                delay: 0.85,
              },
              {
                Icon: ApplePodcastsLogo,
                name: "ap",
                pos: { xPct: 85.62, y: 645 },
                delay: 0.95,
              },
            ] as const
          ).map(({ Icon, name, pos, delay }) => (
            <FloatingTile
              key={name}
              position={pos}
              delay={delay}
              debugLabel={SHOW_TILE_POSITIONS ? name : undefined}
            >
              <PlatformTile Icon={Icon} />
            </FloatingTile>
          ))}
          <FloatingTile position={{ xPct: 10.5, y: 464 }} delay={0.6}>
            <ActionPlanTile
              icon={
                <Pill size={24} weight="regular" className="text-neutral-900" />
              }
              iconSize={24}
              title={t(k.tileCalciumTitle)}
              description={t(k.tileCalciumDesc)}
            />
          </FloatingTile>
          <FloatingTile position={{ xPct: 16.7, y: 585 }} delay={0.7}>
            <BioAgeTile
              label={t(k.tileBioAgeLabel)}
              emphasis={t(k.tileBioAgeEmphasis)}
            />
          </FloatingTile>
          <FloatingTile position={{ xPct: 58.06, y: 432 }} delay={0.8}>
            <BiomarkersTile label={t(k.tileBiomarkersLabel)} />
          </FloatingTile>
          <FloatingTile position={{ xPct: 67.76, y: 579 }} delay={0.9}>
            <ActionPlanTile
              icon={<TestTubeIcon />}
              iconRotation={-15.88}
              title={t(k.tilePotassiumTitle)}
              description={t(k.tilePotassiumDesc)}
            />
          </FloatingTile>
          {/* Exercise tile sits ~36px lower than the other action tiles
              (y 720 vs 579 for Potassium) per round-8 review — opens
              up more breathing room between the Potassium and Exercise
              recommendations vertically, and leaves cleaner space
              for the AP platform circle (y 645) above it. */}
          <FloatingTile position={{ xPct: 62.0, y: 720 }} delay={1.0}>
            <ActionPlanTile
              icon={<DumbbellIcon />}
              title={t(k.tileExerciseTitle)}
              description={t(k.tileExerciseDesc)}
            />
          </FloatingTile>
          <div className="inline-flex items-center gap-2 rounded-full py-1 pr-3 pl-1 backdrop-blur-[5px]">
            <span className="bg-terracotta-500 flex size-[29px] shrink-0 items-center justify-center rounded-full text-white">
              <AheadArrowIcon size={15} />
            </span>
            <span className="type-label-lg text-neutral-900 normal-case">
              {t(k.heroLabel)}
            </span>
          </div>
          <h1 className="mx-auto mt-6 max-w-3xl text-3xl font-normal text-neutral-900 sm:text-5xl md:text-6xl">
            {t(k.heroHeading)}
          </h1>
          <p className="type-body-lg mx-auto mt-6 max-w-2xl text-neutral-900">
            {t(k.heroSubtitleLead)}{" "}
            <strong className="font-bold">{t(k.heroSubtitleEmphasis)}</strong>
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href={AMBASSADOR_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" className="group" iconRight={<NudgeArrow />}>
                {t(k.heroCta)}
              </Button>
            </a>
            <a
              href="#how-it-works"
              onClick={(e) => {
                // Same-page anchor — scroll the How it works section into
                // view smoothly. Falling back to the default `#how-it-works`
                // hash navigation if anything goes wrong (older browsers,
                // scroll-behaviour overrides, etc.).
                const target = document.getElementById("how-it-works")
                if (target) {
                  e.preventDefault()
                  target.scrollIntoView({ behavior: "smooth", block: "start" })
                  history.pushState(null, "", "#how-it-works")
                }
              }}
            >
              <Button size="lg" variant="outline">
                {t(k.heroSecondaryCta)}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ──────────────────── Section 2 — The Ahead Ambassador ──────────────────── */}
      {/*
        Asymmetric padding so the content sits visually centred between
        the hero (which ends with `pb-20` ⇒ 80px of breathing room) and
        section 3 (which starts with `pt-24` ⇒ 96px). To balance:
        `section2.pt = section2.pb + 16` keeps total height equal to
        the original `py-24` (192px) while shifting the content down
        by 8px so the visual gaps above and below match (184px each).
      */}
      <section className="pt-20 pb-12 md:pt-[104px] md:pb-[88px]">
        <div className="mx-auto flex max-w-[1100px] flex-col items-center gap-12 px-4 text-center md:px-10">
          <div className="flex flex-col items-center">
            {/*
              Optical spacing — `type-label-md`'s tight 16px line-height
              sits visually closer to its baseline than the chip badges'
              ~32px height, so a plain `gap-6` reads larger between the
              eyebrow and the chips than between the chips and the
              paragraph. Tighten the eyebrow→chips boundary and let
              chips→body breathe a bit more.
            */}
            <p className="type-label-md text-neutral-600 uppercase">
              {t(k.profileTag)}
            </p>
            <ul className="mt-4 flex flex-wrap items-center justify-center gap-3">
              {PROFILE_CHIPS.map(({ labelKey, icon }) => (
                <li
                  key={labelKey}
                  className="type-label-lg flex items-center gap-1 rounded-full border border-neutral-200 py-1 pr-2.5 pl-1.5 text-neutral-900 normal-case"
                >
                  <Image
                    src={blobMediaUrl(
                      `/images/marketing/ambassadors/${icon}.svg`
                    )}
                    alt=""
                    width={22}
                    height={22}
                    aria-hidden="true"
                    className="size-[22px]"
                  />
                  <span className="pl-0.5">{t(labelKey)}</span>
                </li>
              ))}
            </ul>
            {/*
              Section heading-equivalent: display-sized manifesto sentence
              ending in the heart+tagline. Rendered as <h2> (was <p>) so
              the page's heading outline goes h1 → h2 cleanly rather than
              skipping the profile section's display copy entirely.
            */}
            <h2 className="type-display-md mt-8 font-normal text-neutral-900">
              {t(k.profileBody)} {t(k.profileTaglineLead)}
              {/* Force "like you ❤️" onto its own row. Heart sits AFTER
                  the trail text per Michèle's review (round 1). The
                  trailing period was dropped in round 2 — heart is the
                  sentence terminator now, so adding "." after "you"
                  would read as two stops in a row. */}
              <br />
              {t(k.profileTaglineTrail)}
              {/* Heart PNG: 1024² canvas with ~17% transparent padding
                  on each side; render at 1.6em so the visible glyph
                  reads ~50px on display-md. */}
              <Image
                src={blobMediaUrl("/images/marketing/ambassadors/heart.png")}
                alt=""
                width={112}
                height={112}
                aria-hidden="true"
                className="inline-block align-[-0.45em]"
                style={{
                  width: "1.6em",
                  height: "1.6em",
                  // Spacing between "you" and the heart. The PNG
                  // canvas has ~17% transparent padding on the left of
                  // the visible glyph, so a literal margin of 0 still
                  // reads as a generous gap. -0.45em (round 2) ended
                  // up too tight; round 4 backs it off by ~4px (4/48
                  // ≈ 0.083em at display-md → -0.45 + 0.08 ≈ -0.37em).
                  marginInlineStart: "-0.37em",
                  // Optical baseline nudge + horizontal shift.
                  // - Y 3px: 1px (round 2) read too high after the
                  //   period was dropped; 3px (round 4) sits the
                  //   heart visually on the same baseline as "you".
                  // - X 8px: cumulative nudge — round 5 moved it
                  //   4px right to stop crowding the "u"; round 6
                  //   adds another 4px for more breathing room.
                  //   Done via transform rather than widening the
                  //   margin so the layout box stays put and the
                  //   visible glyph just slides over.
                  transform: "translate(8px, 3px)",
                }}
              />
            </h2>
          </div>
          <a
            href={AMBASSADOR_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="group" iconRight={<NudgeArrow />}>
              {t(k.profileCta)}
            </Button>
          </a>
        </div>
      </section>

      {/* ─────────────────────── Section 3 — How it works ─────────────────────── */}
      {/*
        Full-bleed royal-blue panel (Figma 102:229). Header → cards → CTA
        → cropped phone+vials photo. Each card's icon sits inside a soft
        gradient halo that emits a single radar-style pulse ring when the
        section enters the viewport (Card 2 in Figma is the mid-pulse
        snapshot). The phone shot extends below the section's bottom
        edge (overflow-hidden + negative margin) so we get the framing
        from Figma 107:208 where the device is sliced at mid-frame.
      */}
      <section
        id="how-it-works"
        className="bg-light-blue-800 scroll-mt-24 overflow-hidden px-4 pt-20 pb-0 md:px-10 md:pt-[105px]"
      >
        <div className="mx-auto flex max-w-[1242px] flex-col items-center">
          <div className="flex max-w-[485px] flex-col gap-4 self-start">
            <p className="type-label-md text-white/60 uppercase">
              {t(k.stepsLabel)}
            </p>
            <h2 className="type-display-sm text-white">{t(k.stepsHeading)}</h2>
            <p className="type-body-xl text-white/80">{t(k.stepsSubtitle)}</p>
          </div>
          {/*
            Subgrid alignment (round 10 — Lydia's "text body aligned"
            note): with grid-rows-[auto_auto_1fr] on the parent and
            each card opted into subgrid + row-span-3 at the same
            breakpoint, all icons share row 1, all titles share row 2,
            and all descriptions share row 3 — so a 2-line title in
            one card no longer pushes its description below the
            descriptions of cards with 1-line titles. Mobile (single
            column) doesn't need this — the explicit row tracks only
            apply at sm and above where cards sit side-by-side.
          */}
          <m.ul
            className="mt-12 grid w-full grid-cols-1 gap-4 sm:grid-cols-2 sm:grid-rows-[auto_auto_1fr_auto_auto_1fr] md:mt-[60px] lg:grid-cols-4 lg:grid-rows-[auto_auto_1fr]"
            initial="rest"
            whileInView="show"
            // Hold off until ~half the card row is on-screen so the
            // sequence isn't already over by the time you finish
            // scrolling to it. `amount` (vs `margin`) measures visible
            // fraction of the row itself, so it stays consistent across
            // viewport heights.
            viewport={{ once: true, amount: 0.5 }}
            variants={{
              rest: {},
              show: {
                transition: { staggerChildren: 0.35, delayChildren: 0.15 },
              },
            }}
          >
            {STEPS.map((step, i) => (
              <li
                key={step.titleKey}
                className="border-light-blue-700 bg-light-blue-900 flex flex-col gap-6 rounded-2xl border p-8 sm:row-span-3 sm:grid sm:grid-rows-subgrid lg:row-span-3"
              >
                {/*
                  Icon halo — the soft gradient rim + Phosphor glyph scale
                  in together (0 → 1) from the centre when the section
                  enters view. Staggered via the parent <m.ul>'s
                  staggerChildren so the four cards pop in one-by-one,
                  then sit still (no looping).
                */}
                <m.div
                  className="relative size-[70px] shrink-0"
                  style={{ transformOrigin: "center" }}
                  variants={{
                    rest: { scale: 0, opacity: 0 },
                    show: {
                      scale: 1,
                      opacity: 1,
                      transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
                    },
                  }}
                >
                  <svg
                    viewBox="0 0 70 70"
                    className="absolute inset-0 size-full"
                    aria-hidden="true"
                  >
                    <defs>
                      <radialGradient
                        id={`step-halo-${i}`}
                        cx="0.5"
                        cy="0.5"
                        r="0.5"
                      >
                        <stop offset="28%" stopColor="#fff" stopOpacity="0" />
                        <stop offset="48%" stopColor="#fff" stopOpacity="0" />
                        <stop
                          offset="70%"
                          stopColor="#30409D"
                          stopOpacity="0"
                        />
                        <stop offset="100%" stopColor="#4859BD" />
                      </radialGradient>
                    </defs>
                    <circle
                      cx="35"
                      cy="35"
                      r="34.8"
                      fill={`url(#step-halo-${i})`}
                    />
                  </svg>
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 flex items-center justify-center text-white"
                  >
                    <step.Icon size={36} weight="regular" />
                  </span>
                </m.div>
                <h3 className="type-headline-md text-white">
                  {t(step.titleKey)}
                </h3>
                <p className="type-body-lg text-white/[0.58]">
                  {t(step.descKey)}
                </p>
              </li>
            ))}
          </m.ul>
          {/* CTA pill — uses the DS `tonal` variant for the light pill on
              the dark surface, and the standard arrow-nudge-on-hover that
              every other CTA on the marketing site uses (group + the
              icon's group-hover:translate-x-1). */}
          <a
            href={AMBASSADOR_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-10 md:mt-12"
          >
            <Button
              variant="tonal"
              className="group bg-neutral-100 hover:bg-white"
              iconRight={<NudgeArrow />}
            >
              {t(k.ctaButton)}
            </Button>
          </a>
          {/* Phone+vials shot, cropped at the section's bottom edge per
              Figma 107:208 (overflow-hidden on the section + negative
              bottom margin pulls the section's content box up so the
              section's outer edge slices the photo just below the phone
              screen). Intrinsic PNG is 1232×822; rendered ≤ 648px wide. */}
          <div className="mt-6 -mb-[140px] flex w-full justify-center md:mt-8 md:-mb-[200px]">
            <Image
              src={blobMediaUrl(
                "/images/marketing/ambassadors/how-it-works-phone-vials.png"
              )}
              alt=""
              width={1232}
              height={822}
              aria-hidden="true"
              className="h-auto w-full max-w-[648px]"
            />
          </div>
        </div>
      </section>

      {/* ─────────────────────── Section 4 — Membership benefits ─────────────────────── */}
      {/*
        Reuses the /membership "What's included" grid 1:1 via the shared
        `IncludedFigmaGrid` component (same heading, same 6 image tiles,
        same overlays). `disableLinks` switches every tile from an in-page
        anchor button to a static <div>, because the scroll targets
        (`what-we-test`, `cadence`, `action-plan`, `member-pricing`) live
        on the membership page, not here. The single outline "Learn about
        membership" CTA below the grid is the only navigational affordance.
      */}
      {/*
        Gated on `page:membership`: the whole section advertises the membership
        and its only CTA links to /membership, which doesn't exist in waitlist
        NL (AHD-9361 — "no page, no promise"). Without this the CTA is a dead
        link, which is what the pre-release "all pages load" crawl caught.
        CH (sells it) and DE/AT (coming-soon) keep the section.
      */}
      <CountryGate id="page:membership">
        <section className="relative px-3 py-3 md:px-4 md:py-4">
          <div className="rounded-[24px] bg-white px-4 py-10 md:rounded-[32px] md:px-0 md:py-20">
            <div className="mx-auto max-w-[1440px] md:px-10">
              {/*
                Ambassador-specific subset of the membership benefits grid:
                drops the "follow-ups through the year" (cadence) and
                "upload your existing lab results" tiles per Michèle, so
                only the four core benefits stay (biomarkers, action plan,
                member pricing, health support). With cadence gone, the
                membership page's "Ongoing testing" heading no longer
                fits, so we override with the ambassador-flavour copy.
              */}
              <IncludedFigmaGrid
                disableLinks
                excludeKeys={[INCLUDED_KEYS.cadence, INCLUDED_KEYS.uploadLab]}
                heading={t(k.membershipHeading)}
                eyebrow={t(k.membershipEyebrow)}
              />
              <div className="mt-6 flex justify-center md:mt-8">
                <Link href={lp("/membership")}>
                  <Button variant="outline">{t(k.programGetCta)}</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </CountryGate>

      {/* ──────────────────── Section 5 — Ambassador social content ──────────────────── */}
      {/* All available ambassador content — every video + reel we have.
          Text reviews are suppressed by passing a tag no review
          carries.

          Locale-aware reel ordering is now built into
          TestimonialsSection (preflight #1268): EN/FR/NL viewers
          lead with EN-subtitled clips (Marco, Lucia) and DE viewers
          lead with the native German IG reels (Stefanie, Pierre,
          Nicole). That replaces the earlier `showInstagramOnAllLocales`
          + `videosLeadCarousel` props this page used to set — both
          are gone now since the new default behaviour is exactly
          what /ambassadors wants. */}
      <TestimonialsSection
        cmsTestimonials={initialTestimonials}
        heading={t(k.testimonialsHeading)}
        description={t(k.testimonialsSubtitle)}
      />

      {/* ─────────────────────── Section 6 — FAQ ─────────────────────── */}
      <FaqSection
        heading={t(k.faqHeading)}
        items={initialFaqs}
        currentPage="ambassadors"
      />

      {/* ─────────────────────── Section 7 — Closing CTA ─────────────────────── */}
      {/*
        Page-closing CTA — sits at the very bottom as the final page-wide
        "trailer". Larger vertical padding + display-md heading + lg
        button so it reads as a destination, not a passing banner.
      */}
      <section
        id="apply"
        className="bg-aubergine-800 relative overflow-hidden py-24 text-center md:py-36"
      >
        {/* Photo backdrop + dark overlay — keeps the white heading and
            outline button legible while the portrait shows through. */}
        <Image
          src={blobMediaUrl("/images/marketing/ambassadors/cta-bg.png")}
          alt=""
          fill
          sizes="100vw"
          aria-hidden="true"
          className="absolute inset-0 object-cover"
        />
        <div
          aria-hidden="true"
          className="bg-aubergine-900/55 absolute inset-0"
        />
        <div className="relative mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <h2 className="type-display-md mx-auto max-w-3xl text-white">
              {t(k.ctaHeading)}
            </h2>
            <p className="type-body-lg mx-auto mt-6 max-w-xl text-white/70">
              {t(k.ctaSubtitle)}
            </p>
            <div className="mt-10 flex justify-center">
              <a
                href={AMBASSADOR_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {/* Solid white tonal pill (no magic-stroke ring per
                    round-5 — the gradient ring fought the photo's
                    motion/highlights and read as visual noise; the
                    plain white pill is calmer and still pops). */}
                <Button
                  size="lg"
                  variant="tonal"
                  className="group bg-white hover:bg-white"
                  iconRight={<NudgeArrow />}
                >
                  {t(k.ctaButton)}
                </Button>
              </a>
            </div>
            {/*
              Linkify whatever @aheadhealth.com address the translated
              support copy contains, so contact swaps (Michèle → Lydia →
              …) stay clickable without an accompanying code change.
              Falls back to plain text if no address is present.
            */}
            {(() => {
              const support = t(k.ctaSupport)
              const match = support.match(/[\w.+-]+@aheadhealth\.com/)
              if (!match) {
                return <p className="type-body-lg mt-8 text-white">{support}</p>
              }
              const email = match[0]
              const idx = match.index ?? 0
              return (
                <p className="type-body-lg mt-8 text-white">
                  {support.slice(0, idx)}
                  <a
                    href={`mailto:${email}`}
                    className="underline decoration-white/40 underline-offset-2 transition-colors hover:decoration-white"
                  >
                    {email}
                  </a>
                  {support.slice(idx + email.length)}
                </p>
              )
            })()}
          </div>
        </div>
      </section>
    </>
  )
}
